import React, { useEffect, useMemo } from "react";

import {
  X,
  Store,
  User,
  Phone,
  CalendarDays,
  CreditCard,
  ReceiptText,
  ShoppingBag,
  MapPin,
  Table2,
  CheckCircle2,
  Clock3,
  XCircle,
  Download,
  RotateCcw,
  Utensils,
  MessageSquareText,
  PackageCheck,
  Truck,
  Smartphone,
  Hash,
  BadgeIndianRupee,
  Percent,
  WalletCards,
} from "lucide-react";

/* =========================================================
   HELPERS
========================================================= */

const cn = (...classes) => classes.filter(Boolean).join(" ");

const safeText = (value, fallback = "—") => {
  const text = String(value ?? "").trim();

  return text || fallback;
};

/* =========================================================
   FORMAT AMOUNT
========================================================= */

const formatAmount = (value) => {
  const amount = Number(value ?? 0);

  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
};

/* =========================================================
   FORMAT LABEL
========================================================= */

const formatLabel = (value) =>
  safeText(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

/* =========================================================
   ADDRESS
========================================================= */

const parseAddress = (order) => {
  if (order?.address_line?.trim()) {
    return order.address_line.trim();
  }

  try {
    const values = JSON.parse(order?.address_json || "{}")?.values || {};

    return [
      values.house_number,
      values.building_name,
      values.tower_number && `Tower ${values.tower_number}`,
      values.floor_number && `Floor ${values.floor_number}`,
      values.address,
      values.landmark_area,
      values.city,
      values.state,
      values.in_pin_code,
    ]
      .filter(Boolean)
      .join(", ");
  } catch {
    return "";
  }
};

/* =========================================================
   STATUS
========================================================= */

const getStatusMeta = (status = "") => {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized.includes("completed") ||
    normalized.includes("delivered") ||
    normalized.includes("successful") ||
    normalized.includes("ready") ||
    normalized.includes("paid")
  ) {
    return {
      icon: CheckCircle2,

      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    };
  }

  if (normalized.includes("reject") || normalized.includes("cancel")) {
    return {
      icon: XCircle,

      className: "border-rose-200 bg-rose-50 text-rose-700",
    };
  }

  return {
    icon: Clock3,

    className: "border-amber-200 bg-amber-50 text-amber-700",
  };
};

/* =========================================================
   STATUS PILL
========================================================= */

function StatusPill({ label, value, icon: CustomIcon, tone }) {
  const meta = tone || getStatusMeta(value);

  const Icon = CustomIcon || meta.icon;

  return (
    <span
      className={cn(
        `
          inline-flex
          min-w-0
          max-w-full
          items-center
          gap-1.5
          rounded-full
          border
          px-2.5
          py-1.5
          text-[10px]
          font-semibold
          leading-none

          sm:gap-2
          sm:px-3
          sm:text-xs
        `,
        meta.className,
      )}
    >
      <Icon size={14} strokeWidth={2.2} className="shrink-0" />

      <span className="min-w-0 truncate">
        {label && <span className="mr-1 opacity-60">{label}:</span>}

        {formatLabel(value)}
      </span>
    </span>
  );
}

/* =========================================================
   INFO ROW
========================================================= */

function InfoRow({ icon: Icon, label, value, valueClassName }) {
  return (
    <div
      className="
        flex
        min-w-0
        items-start
        gap-2.5
        rounded-xl
        px-2
        py-2.5
        transition

        hover:bg-slate-50

        sm:gap-3
        sm:px-3
      "
    >
      <div
        className="
          mt-0.5
          flex
          h-8
          w-8
          shrink-0
          items-center
          justify-center
          rounded-lg
          bg-slate-100
          text-slate-600
        "
      >
        <Icon size={16} />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className="
            text-[9px]
            font-semibold
            uppercase
            tracking-[0.08em]
            text-slate-400

            min-[380px]:text-[10px]
            sm:text-[11px]
          "
        >
          {label}
        </p>

        <p
          className={cn(
            `
              mt-0.5
              break-words
              text-[12px]
              font-medium
              leading-5
              text-slate-800

              sm:text-sm
              sm:leading-6
            `,
            valueClassName,
          )}
        >
          {safeText(value)}
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   SUMMARY ROW
========================================================= */

function SummaryRow({ label, value, negative = false, strong = false }) {
  return (
    <div
      className={cn(
        `
          flex
          min-w-0
          items-center
          justify-between
          gap-3
          text-[12px]

          sm:gap-4
          sm:text-sm
        `,
        strong &&
          `
            border-t
            border-dashed
            border-slate-300
            pt-4
            text-sm
            font-bold

            sm:text-base
          `,
      )}
    >
      <span
        className={cn(
          "min-w-0 text-slate-500",

          strong && "text-slate-900",
        )}
      >
        {label}
      </span>

      <span
        className={cn(
          `
            shrink-0
            font-semibold
            text-slate-800
          `,

          negative && "text-emerald-600",

          strong && "text-blue-600",
        )}
      >
        {negative ? "−" : ""}₹{formatAmount(value)}
      </span>
    </div>
  );
}

/* =========================================================
   ORDER DETAILS MODAL
========================================================= */

export default function OrderDetailsModal({
  open,
  onClose,
  order,
  onDownload,
  onReorder,
}) {
  /* =======================================================
     BODY LOCK + ESC
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  /* =======================================================
     DERIVED VALUES
  ======================================================= */

  const address = useMemo(() => parseAddress(order), [order]);

  const items = Array.isArray(order?.items) ? order.items.filter(Boolean) : [];

  const orderStatus = getStatusMeta(order?.status);

  /* =======================================================
     CLOSED
  ======================================================= */

  if (!open || !order) {
    return null;
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div
      role="presentation"
      onMouseDown={onClose}
      className="
    fixed
    inset-0
    z-[999999]
    flex
    h-[100dvh]
    w-full
    items-center
    justify-center
    overflow-hidden
    bg-slate-950/65
    p-3
    backdrop-blur-sm

    sm:p-4
    lg:p-5
  "
    >
      {/* ================================================= */}
      {/* MODAL */}
      {/* ================================================= */}

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-details-title"
        onMouseDown={(event) => event.stopPropagation()}
        className="
    flex
    w-full
    min-w-0
    max-w-6xl
    flex-col
    overflow-hidden

    max-h-[calc(100dvh-24px)]

    rounded-[20px]
    border
    border-white/60
    bg-slate-50
    shadow-2xl

    sm:max-h-[calc(100dvh-32px)]
    sm:rounded-[24px]

    lg:max-h-[calc(100dvh-40px)]
    lg:rounded-[28px]
  "
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <header
          className="
            relative
            shrink-0
            overflow-hidden
            border-b
            border-white/10
            bg-slate-950
            px-4
            py-4
            text-white

            sm:px-5
            sm:py-5

            md:px-6

            lg:px-7
            lg:py-6
          "
        >
          {/* DECORATION */}

          <div
            className="
              pointer-events-none
              absolute
              -right-16
              -top-24
              h-56
              w-56
              rounded-full
              bg-blue-500/25
              blur-3xl
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-24
              left-1/3
              h-48
              w-48
              rounded-full
              bg-violet-500/20
              blur-3xl
            "
          />

          <div
            className="
              relative
              flex
              min-w-0
              items-start
              justify-between
              gap-3

              sm:gap-4
            "
          >
            {/* HEADER CONTENT */}

            <div className="min-w-0 flex-1">
              {/* BADGES */}

              <div
                className="
                  mb-2.5
                  flex
                  min-w-0
                  flex-wrap
                  items-center
                  gap-1.5

                  sm:mb-3
                  sm:gap-2
                "
              >
                <span
                  className="
                    inline-flex
                    max-w-full
                    items-center
                    gap-1.5
                    rounded-full
                    border
                    border-white/15
                    bg-white/10
                    px-2
                    py-1
                    text-[10px]
                    font-semibold
                    text-slate-200

                    sm:px-2.5
                    sm:text-xs
                  "
                >
                  <Hash size={12} className="shrink-0" />

                  <span className="truncate">{safeText(order.orderid)}</span>
                </span>

                <StatusPill value={order.status} tone={orderStatus} />
              </div>

              {/* TITLE */}

              <h2
                id="order-details-title"
                className="
                  truncate
                  text-lg
                  font-bold
                  tracking-tight

                  min-[380px]:text-xl

                  sm:text-2xl
                "
              >
                Order details
              </h2>

              <p
                className="
                  mt-1
                  max-w-[650px]
                  text-[10px]
                  leading-4
                  text-slate-300

                  min-[380px]:text-[11px]

                  sm:text-sm
                  sm:leading-5
                "
              >
                Complete order, customer and payment information
              </p>
            </div>

            {/* CLOSE */}

            <button
              type="button"
              onClick={onClose}
              aria-label="Close order details"
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-xl
                border
                border-white/15
                bg-white/10
                text-white
                transition

                hover:bg-white/20

                focus:outline-none
                focus:ring-2
                focus:ring-white/50

                sm:h-10
                sm:w-10
              "
            >
              <X size={19} />
            </button>
          </div>
        </header>

        {/* ================================================= */}
        {/* SCROLLABLE BODY */}
        {/* ================================================= */}

        <div
          className="
            min-h-0
            flex-1
            overscroll-contain
            overflow-y-auto

            [scrollbar-width:none]
            [-ms-overflow-style:none]
            [&::-webkit-scrollbar]:hidden
          "
        >
          <div
            className="
              space-y-4
              p-3
              pb-5

              min-[380px]:p-4

              sm:space-y-5
              sm:p-5

              md:p-6

              lg:p-6
            "
          >
            {/* ================================================= */}
            {/* RESTAURANT + CUSTOMER */}
            {/* ================================================= */}

            <div
              className="
                grid
                grid-cols-1
                gap-3

                md:grid-cols-2
                md:gap-4
              "
            >
              {/* RESTAURANT */}

              <article
                className="
                  min-w-0
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  p-3
                  shadow-sm

                  sm:p-4

                  lg:p-5
                "
              >
                <div
                  className="
                    mb-3
                    flex
                    min-w-0
                    items-center
                    gap-3
                    border-b
                    border-slate-100
                    pb-3

                    sm:mb-4
                    sm:pb-4
                  "
                >
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-blue-50
                      text-blue-600

                      sm:h-11
                      sm:w-11
                    "
                  >
                    <Store size={20} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className="
                        text-[9px]
                        font-semibold
                        uppercase
                        tracking-[0.12em]
                        text-blue-600

                        sm:text-xs
                      "
                    >
                      Restaurant
                    </p>

                    <h3
                      className="
                        truncate
                        text-sm
                        font-bold
                        text-slate-900

                        min-[380px]:text-base

                        sm:text-lg
                      "
                    >
                      {safeText(order.organization_name, "Restaurant")}
                    </h3>
                  </div>
                </div>

                <div
                  className="
                    grid
                    grid-cols-1
                    gap-0.5

                    min-[480px]:grid-cols-2

                    md:grid-cols-1

                    lg:grid-cols-2
                  "
                >
                  <InfoRow
                    icon={Hash}
                    label="Order ID"
                    value={`#${safeText(order.orderid)}`}
                  />

                  <InfoRow
                    icon={ShoppingBag}
                    label="Order type"
                    value={formatLabel(order.orderType)}
                  />

                  <InfoRow
                    icon={Smartphone}
                    label="Platform"
                    value={formatLabel(order.platform)}
                  />

                  <InfoRow icon={Table2} label="Table" value={order.table_no} />
                </div>
              </article>

              {/* CUSTOMER */}

              <article
                className="
                  min-w-0
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  p-3
                  shadow-sm

                  sm:p-4

                  lg:p-5
                "
              >
                <div
                  className="
                    mb-3
                    flex
                    min-w-0
                    items-center
                    gap-3
                    border-b
                    border-slate-100
                    pb-3

                    sm:mb-4
                    sm:pb-4
                  "
                >
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-violet-50
                      text-violet-600

                      sm:h-11
                      sm:w-11
                    "
                  >
                    <User size={20} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className="
                        text-[9px]
                        font-semibold
                        uppercase
                        tracking-[0.12em]
                        text-violet-600

                        sm:text-xs
                      "
                    >
                      Customer
                    </p>

                    <h3
                      className="
                        truncate
                        text-sm
                        font-bold
                        text-slate-900

                        min-[380px]:text-base

                        sm:text-lg
                      "
                    >
                      {safeText(order.customer_name, "Guest customer")}
                    </h3>
                  </div>
                </div>

                <div className="space-y-0.5">
                  <InfoRow
                    icon={Phone}
                    label="Mobile number"
                    value={order.customer_mobile}
                  />

                  <InfoRow
                    icon={CalendarDays}
                    label="Order date"
                    value={order.order_date}
                  />

                  <InfoRow
                    icon={MapPin}
                    label="Delivery address"
                    value={address}
                  />
                </div>
              </article>
            </div>

            {/* ================================================= */}
            {/* PAYMENT HEADING */}
            {/* ================================================= */}

            <div
              className="
                flex
                items-center
                gap-3
                pt-1
              "
            >
              <div
                className="
                  flex
                  h-9
                  w-9
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  bg-violet-50
                  text-violet-600

                  sm:h-10
                  sm:w-10
                "
              >
                <CreditCard size={18} />
              </div>

              <div className="min-w-0">
                <h3
                  className="
                    text-sm
                    font-bold
                    text-slate-900

                    sm:text-base
                  "
                >
                  Payment & delivery
                </h3>

                <p
                  className="
                    mt-0.5
                    text-[10px]
                    text-slate-500

                    sm:text-xs
                  "
                >
                  Transaction and fulfilment information
                </p>
              </div>
            </div>

            {/* ================================================= */}
            {/* PAYMENT */}
            {/* ================================================= */}

            <div
              className="
                min-w-0
                rounded-2xl
                border
                border-slate-200
                bg-white
                p-3
                shadow-sm

                sm:p-4
              "
            >
              {/* STATUS */}

              <div
                className="
                  flex
                  min-w-0
                  flex-wrap
                  gap-1.5

                  sm:gap-2
                "
              >
                <StatusPill label="Order" value={order.status} />

                <StatusPill
                  label="Payment"
                  value={order.payment_status}
                  icon={WalletCards}
                />

                <StatusPill
                  label="Mode"
                  value={order.payment_mode}
                  icon={CreditCard}
                  tone={{
                    className: "border-blue-200 bg-blue-50 text-blue-700",
                  }}
                />

                <StatusPill
                  label="Delivery"
                  value={order.delivery_partner_status}
                  icon={Truck}
                  tone={{
                    className: "border-violet-200 bg-violet-50 text-violet-700",
                  }}
                />
              </div>

              {/* PAYMENT INFORMATION */}

              <div
                className="
                  mt-3
                  grid
                  grid-cols-1
                  gap-1

                  min-[480px]:grid-cols-2

                  lg:grid-cols-3

                  xl:grid-cols-5
                "
              >
                <InfoRow
                  icon={CreditCard}
                  label="Payment mode"
                  value={formatLabel(order.payment_mode)}
                />

                <InfoRow
                  icon={BadgeIndianRupee}
                  label="Payment status"
                  value={formatLabel(order.payment_status)}
                />

                <InfoRow
                  icon={Truck}
                  label="Delivery status"
                  value={formatLabel(order.delivery_partner_status)}
                />

                <InfoRow
                  icon={Clock3}
                  label="Delivery time"
                  value={
                    order?.delivery_time
                      ? `${order.delivery_time} mins`
                      : "Not available"
                  }
                />

                <InfoRow
                  icon={Percent}
                  label="Discount"
                  value={`${formatAmount(order.discount_percentage)}%`}
                />
              </div>
            </div>

            {/* ================================================= */}
            {/* ITEMS + BILL SUMMARY */}
            {/* ================================================= */}

            <div
              className="
                grid
                grid-cols-1
                gap-4

                lg:gap-5

                xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.75fr)]
              "
            >
              {/* ================================================= */}
              {/* ITEMS */}
              {/* ================================================= */}

              <article
                className="
                  min-w-0
                  overflow-hidden
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  shadow-sm
                "
              >
                {/* ITEMS HEADER */}

                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    justify-between
                    gap-2
                    border-b
                    border-slate-100
                    px-3
                    py-3

                    sm:gap-3
                    sm:px-4
                    sm:py-4

                    lg:px-5
                  "
                >
                  <div
                    className="
                      flex
                      min-w-0
                      items-center
                      gap-2.5

                      sm:gap-3
                    "
                  >
                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-blue-50
                        text-blue-600

                        sm:h-10
                        sm:w-10
                      "
                    >
                      <ShoppingBag size={18} />
                    </div>

                    <div className="min-w-0">
                      <h3
                        className="
                          truncate
                          text-sm
                          font-bold
                          text-slate-900

                          sm:text-base
                        "
                      >
                        Ordered items
                      </h3>

                      <p
                        className="
                          mt-0.5
                          hidden
                          text-[10px]
                          text-slate-500

                          min-[380px]:block

                          sm:text-xs
                        "
                      >
                        Items included in this order
                      </p>
                    </div>
                  </div>

                  <span
                    className="
                      shrink-0
                      rounded-full
                      bg-slate-100
                      px-2
                      py-1
                      text-[10px]
                      font-bold
                      text-slate-600

                      sm:px-2.5
                      sm:text-xs
                    "
                  >
                    {items.length} {items.length === 1 ? "item" : "items"}
                  </span>
                </div>

                {/* ITEMS LIST */}

                {items.length ? (
                  <div className="divide-y divide-slate-100">
                    {items.map((item, index) => (
                      <div
                        key={item.id || `${item.product_id}-${index}`}
                        className="
                            flex
                            min-w-0
                            gap-2.5
                            p-3

                            sm:gap-4
                            sm:p-4

                            lg:p-5
                          "
                      >
                        {/* IMAGE */}

                        <div
                          className="
                              flex
                              h-12
                              w-12
                              shrink-0
                              items-center
                              justify-center
                              overflow-hidden
                              rounded-xl
                              border
                              border-slate-200
                              bg-slate-50
                              text-slate-500

                              min-[380px]:h-14
                              min-[380px]:w-14

                              sm:rounded-2xl
                            "
                        >
                          {item.itemImage ? (
                            <img
                              src={item.itemImage}
                              alt={item.item_name || "Order item"}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Utensils size={20} />
                          )}
                        </div>

                        {/* ITEM CONTENT */}

                        <div className="min-w-0 flex-1">
                          <div
                            className="
                                flex
                                min-w-0
                                items-start
                                justify-between
                                gap-2

                                sm:gap-3
                              "
                          >
                            <div className="min-w-0 flex-1">
                              <h4
                                className="
                                    line-clamp-2
                                    break-words
                                    text-[12px]
                                    font-semibold
                                    leading-4
                                    text-slate-900

                                    min-[380px]:text-[13px]

                                    sm:text-sm
                                    sm:leading-5
                                  "
                              >
                                {safeText(
                                  item.item_name ||
                                    item.actualName ||
                                    item.product_id,
                                  "Item",
                                )}
                              </h4>

                              <p
                                className="
                                    mt-1
                                    text-[10px]
                                    leading-4
                                    text-slate-500

                                    sm:text-xs
                                  "
                              >
                                Qty {safeText(item.quantity, "0")} × ₹
                                {formatAmount(item.item_price)}
                              </p>
                            </div>

                            <p
                              className="
                                  shrink-0
                                  whitespace-nowrap
                                  text-[12px]
                                  font-bold
                                  text-slate-900

                                  sm:text-base
                                "
                            >
                              ₹{formatAmount(item.total_price)}
                            </p>
                          </div>

                          {/* CUSTOMIZATION */}

                          {item.customization &&
                            item.customization !== "{}" && (
                              <div
                                className="
                                    mt-2
                                    flex
                                    min-w-0
                                    items-start
                                    gap-2
                                    rounded-xl
                                    bg-amber-50
                                    px-2.5
                                    py-2
                                    text-[10px]
                                    leading-4
                                    text-amber-800

                                    sm:mt-3
                                    sm:px-3
                                    sm:text-xs
                                  "
                              >
                                <MessageSquareText
                                  size={14}
                                  className="mt-0.5 shrink-0"
                                />

                                <span className="min-w-0 break-words">
                                  {item.customization}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="
                      flex
                      min-h-[180px]
                      flex-col
                      items-center
                      justify-center
                      px-5
                      py-10
                      text-center
                    "
                  >
                    <PackageCheck size={32} className="text-slate-300" />

                    <p
                      className="
                        mt-3
                        text-sm
                        font-semibold
                        text-slate-700
                      "
                    >
                      No item details available
                    </p>
                  </div>
                )}
              </article>

              {/* ================================================= */}
              {/* BILL */}
              {/* ================================================= */}

              <aside
                className="
                  min-w-0
                  self-start
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  p-4
                  shadow-sm

                  sm:p-5

                  xl:sticky
                  xl:top-0
                "
              >
                <div
                  className="
                    mb-4
                    flex
                    items-center
                    gap-3

                    sm:mb-5
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-emerald-50
                      text-emerald-600

                      sm:h-10
                      sm:w-10
                    "
                  >
                    <ReceiptText size={18} />
                  </div>

                  <div className="min-w-0">
                    <h3
                      className="
                        text-sm
                        font-bold
                        text-slate-900

                        sm:text-base
                      "
                    >
                      Bill summary
                    </h3>

                    <p
                      className="
                        mt-0.5
                        text-[10px]
                        text-slate-500

                        sm:text-xs
                      "
                    >
                      Price and tax breakdown
                    </p>
                  </div>
                </div>

                <div
                  className="
                    space-y-3

                    sm:space-y-3.5
                  "
                >
                  <SummaryRow label="Subtotal" value={order.subtotal} />

                  <SummaryRow label="Delivery fee" value={order.delivery_fee} />

                  <SummaryRow
                    label="Discount"
                    value={order.discount_amount}
                    negative
                  />

                  <SummaryRow
                    label="Packaging"
                    value={order.packaging_charges}
                  />

                  <SummaryRow
                    label="SGST (2%)"
                    value={Number(order.subtotal || 0) * 0.02}
                  />

                  <SummaryRow
                    label="CGST (2%)"
                    value={Number(order.subtotal || 0) * 0.02}
                  />

                  <SummaryRow
                    label="Total paid"
                    value={order.pay_amount}
                    strong
                  />
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* FOOTER */}
        {/* ================================================= */}

        <footer
          className="
            shrink-0
            border-t
            border-slate-200
            bg-white/95
            px-3
            py-3
            pb-[max(12px,env(safe-area-inset-bottom))]
            backdrop-blur

            sm:px-5
            sm:py-3.5

            md:px-6

            lg:py-4
          "
        >
          <div
            className="
              flex
              flex-col
              gap-2.5

              sm:flex-row
              sm:items-center
              sm:justify-between
              sm:gap-3
            "
          >
            <p
              className="
                hidden
                text-xs
                text-slate-400

                md:block
              "
            >
              Press Esc or click outside to close
            </p>

            <div
              className="
                grid
                w-full
                grid-cols-2
                gap-2

                sm:ml-auto
                sm:w-auto
                sm:flex
                sm:items-center
              "
            >
              {/* CLOSE */}

              <button
                type="button"
                onClick={onClose}
                className="
                  inline-flex
                  h-10
                  min-w-0
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-slate-300
                  bg-white
                  px-3
                  text-[12px]
                  font-semibold
                  text-slate-700
                  transition

                  hover:bg-slate-50

                  focus:outline-none
                  focus:ring-2
                  focus:ring-slate-300

                  sm:h-11
                  sm:min-w-[90px]
                  sm:px-4
                  sm:text-sm
                "
              >
                Close
              </button>

              {/* DOWNLOAD */}

              <button
                type="button"
                onClick={() => onDownload?.(order)}
                disabled={!onDownload}
                className="
                  inline-flex
                  h-10
                  min-w-0
                  items-center
                  justify-center
                  gap-1.5
                  rounded-xl
                  bg-slate-900
                  px-3
                  text-[12px]
                  font-semibold
                  text-white
                  transition

                  hover:bg-slate-800

                  disabled:cursor-not-allowed
                  disabled:opacity-50

                  sm:h-11
                  sm:min-w-[120px]
                  sm:gap-2
                  sm:px-4
                  sm:text-sm
                "
              >
                <Download size={16} className="shrink-0" />

                <span>Download</span>
              </button>

              {/* REORDER */}

              <button
                type="button"
                onClick={() => onReorder?.(order)}
                disabled={!onReorder}
                className="
                  col-span-2
                  inline-flex
                  h-10
                  min-w-0
                  items-center
                  justify-center
                  gap-1.5
                  rounded-xl
                  bg-blue-600
                  px-3
                  text-[12px]
                  font-semibold
                  text-white
                  shadow-sm
                  shadow-blue-200
                  transition

                  hover:bg-blue-700

                  disabled:cursor-not-allowed
                  disabled:opacity-50

                  sm:h-11
                  sm:min-w-[110px]
                  sm:gap-2
                  sm:px-4
                  sm:text-sm
                "
              >
                <RotateCcw size={16} className="shrink-0" />
                Reorder
              </button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
